import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Container } from "../components/Container";
import { SectionHeading } from "../components/SectionHeading";

type FAQItem = { q: string; a: string };

export function FAQ() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const reduce = useReducedMotion();

  const items = (t("faq.items", { returnObjects: true }) as FAQItem[]) ?? [];

  return (
    <section id="faq" className="py-14 sm:py-20">
      <Container size="md">
        <SectionHeading
          eyebrow={t("faq.eyebrow")}
          title={t("faq.section_title")}
          description={t("faq.section_description")}
        />

        <ul className="mt-14">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <li
                key={`${item.q}-${i}`}
                className="border-b border-border-soft last:border-b-0"
              >
                <h3>
                  <button
                    id={buttonId}
                    type="button"
                    aria-controls={panelId}
                    aria-expanded={isOpen}
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="flex w-full items-center justify-between gap-6 py-5 text-start text-base font-semibold text-text-heading transition-colors duration-150 hover:text-brand-500 sm:py-6 sm:text-lg"
                  >
                    <span>{item.q}</span>
                    <motion.span
                      aria-hidden="true"
                      animate={reduce ? undefined : { rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-bg-muted text-text-primary dark:bg-bg-subtle"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.span>
                  </button>
                </h3>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      key="content"
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      initial={
                        reduce ? { opacity: 1 } : { height: 0, opacity: 0 }
                      }
                      animate={
                        reduce
                          ? { opacity: 1 }
                          : { height: "auto", opacity: 1 }
                      }
                      exit={
                        reduce ? { opacity: 1 } : { height: 0, opacity: 0 }
                      }
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <p className="pb-6 pe-12 text-base leading-relaxed text-text-muted">
                        {item.a}
                      </p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>
      </Container>
    </section>
  );
}
